import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { AlertCardTrade, AlertHistoryItem } from '../types/AlertHistoryItem'
import {
  formatAlertDateTime,
  formatAlertPrice,
  formatAlertProfit,
  formatLotSize,
} from '../utils/alertFormatters'
import AlertCardSectionTitle from './AlertCardSectionTitle'
import TradeExitIcon from './TradeExitIcon'
import { getTradeExitKind } from '../utils/tradeExit'
import { describeTradeRejection } from '../utils/tradeRejection'

// Fixed row metrics so the list clips on a row boundary instead of mid-row
export const RECENT_ALERT_ROW_HEIGHT = 42
export const RECENT_ALERT_ROW_GAP = 4

// A fire this recent gets a NEW badge; re-evaluated on a timer so the badge drops off by itself
const NEW_BADGE_WINDOW_MS = 5 * 60 * 1000
const NEW_BADGE_TICK_MS = 30 * 1000

// Ticks only while `enabled`, so cards with nothing inside the NEW window hold no timer
const useNow = (tickMs: number, enabled: boolean) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => setNow(Date.now()), tickMs)
    return () => clearInterval(id)
  }, [tickMs, enabled])
  return now
}

interface RecentAlertRowProps {
  item: AlertHistoryItem
  trade?: AlertCardTrade
  isNew: boolean
}

const RecentAlertRow: React.FC<RecentAlertRowProps> = ({ item, trade, isNew }) => {
  const theme = useTheme()
  const sideColor = item.type === 'buy' ? theme.palette.success.main : theme.palette.error.main
  const profitColor =
    trade?.profit === undefined || trade.profit === 0
      ? theme.palette.divider
      : trade.profit > 0
        ? theme.palette.success.main
        : theme.palette.error.main
  const rejection = trade?.status === 'REJECTED' ? describeTradeRejection(trade) : null
  const statusColor =
    trade?.status === 'REJECTED'
      ? theme.palette.error.main
      : trade?.status === 'PENDING'
        ? theme.palette.warning.main
        : theme.palette.text.primary

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        height: RECENT_ALERT_ROW_HEIGHT,
        flexShrink: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        pl: 1.25,
        pr: 1,
        borderLeft: `2px solid ${trade ? profitColor : sideColor}`,
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '0 4px 4px 0',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: sideColor }}>
            {item.type === 'buy' ? 'Buy' : 'Sell'}
          </Typography>
          {trade?.lotSize !== undefined && (
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: sideColor }}>
              {formatLotSize(trade.lotSize)}
            </Typography>
          )}
          {isNew && (
            <Box
              component="span"
              sx={{
                fontSize: '9px',
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: '0.06em',
                px: 0.5,
                py: 0.25,
                borderRadius: '3px',
                color: theme.palette.common.black,
                backgroundColor: theme.palette.primary.main,
              }}
            >
              NEW
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary }}>
          {formatAlertDateTime(item.time)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        {trade ? (
          <>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline', minWidth: 0 }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: statusColor }}>
                {trade.status}
              </Typography>
              {(trade.status === 'OPEN' || trade.status === 'CLOSED') &&
                trade.profit !== undefined && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: profitColor }}>
                    {formatAlertProfit(trade.profit)}
                  </Typography>
                )}
              {rejection && (
                <Typography
                  noWrap
                  title={rejection.tooltip}
                  sx={{ fontSize: '10px', fontStyle: 'italic', color: theme.palette.error.main }}
                >
                  {rejection.text}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary }}>
                {trade.exitPrice === undefined
                  ? `$${formatAlertPrice(trade.entryPrice)}`
                  : `$${formatAlertPrice(trade.entryPrice)} → $${formatAlertPrice(trade.exitPrice)}`}
              </Typography>
              <TradeExitIcon exitKind={getTradeExitKind(trade, item.type)} size={12} />
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary }}>
            @ ${formatAlertPrice(item.price)}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

interface RecentAlertsListProps {
  items: AlertHistoryItem[]
  tradesByNotificationId?: Record<string, AlertCardTrade>
  loading?: boolean
  onTitleClick: () => void
}

// Newest fires of an alert; scrolls within the card when the rows need more room than it has
const RecentAlertsList: React.FC<RecentAlertsListProps> = ({
  items,
  tradesByNotificationId = {},
  loading = false,
  onTitleClick,
}) => {
  const theme = useTheme()
  const hasNewCandidate = items.some((item) => Date.now() - item.time < NEW_BADGE_WINDOW_MS)
  const now = useNow(NEW_BADGE_TICK_MS, hasNewCandidate)
  const title = items.length === 1 ? 'Recent Notification' : 'Recent Notifications'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <AlertCardSectionTitle
        title={title}
        onClick={onTitleClick}
        endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
      />
      {items.length === 0 ? (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.disabled, textAlign: 'center', py: 1 }}
        >
          {loading ? 'Loading…' : 'No notifications yet'}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${RECENT_ALERT_ROW_GAP}px`,
            overflowY: 'auto',
            minHeight: 0,
            pr: 0.25,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.background.custom4 || '#383B42',
              borderRadius: '2px',
            },
          }}
        >
          {items.map((item, index) => (
            <RecentAlertRow
              key={`${item.time}-${index}`}
              item={item}
              trade={item.id ? tradesByNotificationId[item.id] : undefined}
              isNew={now - item.time < NEW_BADGE_WINDOW_MS}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default RecentAlertsList
